import {
  BatteryFullIcon,
  BriefcaseConveyorBeltIcon,
  BriefcaseMedicalIcon,
  CalendarIcon,
  CogIcon,
  MapPinIcon,
  SearchIcon,
  SignalIcon,
  StarIcon,
  TrendingUpIcon,
  UserCheckIcon,
  WifiIcon,
} from "lucide-react";

import { Column, Row } from "@/components";
import { cn } from "@/lib/utils";
import { CompensationType, ShiftType, SignupVariantBadge } from "@/types";

const BADGE_ORDER = [
  SignupVariantBadge.HIRING_MULTIPLE_ROLES,
  SignupVariantBadge.FULL_MEDICAL_DENTAL_VISION,
  SignupVariantBadge.FAST_GROWING_COMPANY,
];

const SHIFT_ORDER = [
  ShiftType.FIRSTSHIFT,
  ShiftType.SECONDSHIFT,
  ShiftType.THIRDSHIFT,
  ShiftType.WEEKEND,
];

const BADGE_CONFIG: Record<
  string,
  { label: string; icon: React.ElementType; iconClass: string; textClass: string; bgClass: string }
> = {
  [SignupVariantBadge.HIRING_MULTIPLE_ROLES]: {
    label: "Hiring Multiple Roles",
    icon: UserCheckIcon,
    iconClass: "text-emerald-600",
    textClass: "text-emerald-700",
    bgClass: "bg-emerald-50",
  },
  [SignupVariantBadge.FULL_MEDICAL_DENTAL_VISION]: {
    label: "Full Medical, Dental, Vision",
    icon: BriefcaseMedicalIcon,
    iconClass: "text-amber-600",
    textClass: "text-amber-700",
    bgClass: "bg-amber-50",
  },
  [SignupVariantBadge.FAST_GROWING_COMPANY]: {
    label: "Fast Growing Company",
    icon: TrendingUpIcon,
    iconClass: "text-blue-600",
    textClass: "text-blue-700",
    bgClass: "bg-blue-50",
  },
};

const SHIFT_SHORT_LABELS: Record<ShiftType, string> = {
  [ShiftType.FIRSTSHIFT]: "1st",
  [ShiftType.SECONDSHIFT]: "2nd",
  [ShiftType.THIRDSHIFT]: "3rd",
  [ShiftType.WEEKEND]: "Weekend",
};

export const JobSignupPreview = ({
  badges,
  buttonText,
  compensationMax,
  compensationMin,
  compensationType,
  ctaLabel,
  header,
  heroImageUrl,
  location,
  relocationAvailable,
  relocationMax,
  relocationMin,
  shifts,
  subHeader,
  theWork,
  whatWereLookingFor,
  whyThisShop,
}: {
  badges: string[];
  buttonText: string;
  compensationMax: string;
  compensationMin: string;
  compensationType: CompensationType;
  ctaLabel: string;
  header: string;
  heroImageUrl: string;
  location: string;
  relocationAvailable: boolean;
  relocationMax: string;
  relocationMin: string;
  shifts: string[];
  subHeader: string;
  theWork: string;
  whatWereLookingFor: string;
  whyThisShop: string;
}) => {
  const compensationMinimumNumber = Number(compensationMin);
  const compensationMaximumNumber = Number(compensationMax);
  const compensationMinimumDisplay =
    compensationMin.trim() !== "" && Number.isFinite(compensationMinimumNumber)
      ? compensationMinimumNumber.toLocaleString()
      : "XX";
  const compensationMaximumDisplay =
    compensationMax.trim() !== "" && Number.isFinite(compensationMaximumNumber)
      ? compensationMaximumNumber.toLocaleString()
      : "XX";

  const relocationMinimumNumber = Number(relocationMin);
  const relocationMaximumNumber = Number(relocationMax);
  const relocationMinimumDisplay =
    relocationMin.trim() !== "" && Number.isFinite(relocationMinimumNumber)
      ? relocationMinimumNumber.toLocaleString()
      : "XX";
  const relocationMaximumDisplay =
    relocationMax.trim() !== "" && Number.isFinite(relocationMaximumNumber)
      ? relocationMaximumNumber.toLocaleString()
      : "XX";
  const hasRelocationAmounts =
    relocationMin.trim() !== "" || relocationMax.trim() !== "";

  const shiftLabels = SHIFT_ORDER
    .filter((shift) => shifts.includes(shift))
    .map((shift) => SHIFT_SHORT_LABELS[shift])
    .filter(Boolean);

  return (
    <Column className="items-center">
      {/* Phone Frame */}
      <div className="w-[300px] overflow-hidden rounded-2xl border-[3px] border-slate-800">
        {/* Status Bar */}
        <Row className="items-center justify-between bg-white px-6 pb-1 pt-3">
          <span className="text-xs font-semibold text-slate-900">9:41</span>
          <Row className="items-center gap-1">
            <SignalIcon className="h-3 w-3 text-slate-900" />
            <WifiIcon className="h-3 w-3 text-slate-900" />
            <BatteryFullIcon className="h-3 w-3 text-slate-900" />
          </Row>
        </Row>

        {/* Scrollable Content */}
        <div className="h-[560px] overflow-y-auto bg-white">
          {/* Hero Image with Location Badge */}
          <div className="relative mx-3 mt-2 overflow-hidden rounded-xl">
            {heroImageUrl ? (
              <img
                alt="Hero"
                className="h-[150px] w-full object-cover"
                src={heroImageUrl}
              />
            ) : (
              <Row className="h-[150px] w-full items-center justify-center bg-slate-200">
                <span className="text-xs text-slate-400">Hero Image</span>
              </Row>
            )}
            {(
              <Row className="absolute left-3 top-3 items-center gap-1 rounded-full">
                <MapPinIcon className="h-3 w-3 text-white" />
                <span className="text-[9px] font-medium uppercase tracking-wide text-white">
                  {location || "Location"}
                </span>
              </Row>
            )}
          </div>

          {/* Title Area */}
          <div className="px-4 pt-3">
            <Row className="items-start justify-between">
              <div>
                <p className="text-sm font-bold text-slate-800">
                  {header || "Job Title"}
                </p>
                <p className="text-[11px] text-slate-500">
                  {subHeader || "Industry"}
                </p>
              </div>
              <Column className="items-end">
                <p className="text-xs font-bold text-slate-800">
                  {`$${compensationMinimumDisplay} - $${compensationMaximumDisplay}`}
                </p>
                <p className="text-[9px] uppercase text-slate-400">
                  {compensationType === CompensationType.YEARLY
                    ? "Per Year"
                    : "Per Hour"}
                </p>
              </Column>
            </Row>
          </div>

          {/* Badges + Shifts */}
          {(badges.length > 0 || shiftLabels.length > 0) && (
            <Column className="gap-1.5 px-4 pt-3">
              {BADGE_ORDER.filter((b) => badges.includes(b)).map((badge) => {
                const config = BADGE_CONFIG[badge];
                const Icon = config.icon;
                return (
                  <div
                    className={cn(
                      "inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1",
                      config.bgClass,
                    )}
                    key={badge}
                  >
                    <Icon className={`h-3 w-3 ${config.iconClass}`} />
                    <span
                      className={`text-[10px] font-medium ${config.textClass}`}
                    >
                      {config.label}
                    </span>
                  </div>
                );
              })}
              {shiftLabels.length > 0 && (
                <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1">
                  <CalendarIcon className="h-3 w-3 text-slate-500" />
                  <span className="text-[10px] font-medium text-slate-600">
                    {`${shiftLabels.join(", ")} ${shiftLabels.length === 1 ? "Shift" : "Shifts"}`}
                  </span>
                </div>
              )}
            </Column>
          )}

          {/* The Work Section */}
          <div className="px-4 pt-4">
            <Row className="items-center gap-1.5">
              <CogIcon className="h-3 w-3 text-slate-700" />
              <p className="text-xs font-bold text-slate-800">The Work</p>
            </Row>
            <p className="mt-1 whitespace-pre-wrap text-[10px] leading-relaxed text-slate-600">
              {theWork || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."}
            </p>
          </div>

          {/* Relocation Section */}
          {relocationAvailable && (
            <div className="mx-4 mt-3 rounded-lg bg-blue-50 p-3">
              <Row className="items-center gap-1.5">
                <BriefcaseConveyorBeltIcon className="h-3 w-3 text-blue-600" />
                <p className="text-[10px] font-bold text-slate-800">
                  Relocation
                </p>
              </Row>
              <p className="mt-0.5 text-[9px] leading-relaxed text-slate-600">
                {hasRelocationAmounts ? (
                  <>
                    <span className="font-semibold text-blue-600">
                      ${relocationMinimumDisplay} - ${relocationMaximumDisplay} package available
                    </span>
                    . Open to candidates from anywhere. Interview travel
                    expenses reimbursed.
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-blue-600">
                      Relocation package available
                    </span>
                    . Open to candidates from anywhere.
                  </>
                )}
              </p>
            </div>
          )}

          {/* What We're Looking For Section */}
          <div className="px-4 pt-4">
            <Row className="items-center gap-1.5">
              <SearchIcon className="h-3 w-3 text-slate-700" />
              <p className="text-xs font-bold text-slate-800">
                What We&apos;re Looking For
              </p>
            </Row>
            <p className="mt-1 whitespace-pre-wrap text-[10px] leading-relaxed text-slate-600">
              {whatWereLookingFor || "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."}
            </p>
          </div>

          {/* Why This Shop */}
          <div className="px-4 pt-4">
            <Row className="items-center gap-1.5">
              <StarIcon className="h-3 w-3 text-slate-700" />
              <p className="text-xs font-bold text-slate-800">
                Why This Shop
              </p>
            </Row>
            <p className="mt-1 whitespace-pre-wrap text-[10px] leading-relaxed text-slate-600">
              {whyThisShop || "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."}
            </p>
          </div>

          {/* Bottom Padding for Button */}
          <div className="h-4" />
        </div>

        {/* Sticky CTA Button */}
        <div className="border-t border-slate-100 bg-white px-4 py-3">
          {ctaLabel && (
            <p className="mb-1.5 text-center text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              {ctaLabel}
            </p>
          )}
          <div className="rounded-full bg-emerald-700 py-2.5 text-center">
            <span className="text-xs font-semibold text-white">
              {buttonText || "I'm Interested"}
            </span>
          </div>
        </div>
      </div>
    </Column>
  );
};
